/**
 * TOSS Web Application - API Types
 * Common types for API requests and responses
 */

import { UserRole } from './enums';
import { User, UserShort } from './user.types';

// ============================================================================
// Generic API Response Types
// ============================================================================

/**
 * Standard paginated response from API
 */
export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * Standard API error response
 */
export interface ApiError {
  message: string;
  code?: string;
  errors?: Record<string, string[]>;
  statusCode: number;
}

/**
 * Validation error details
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Standard success response for mutations
 */
export interface SuccessResponse {
  success: boolean;
  message?: string;
}

/**
 * Response with created entity ID
 */
export interface CreatedResponse {
  id: string;
  success: boolean;
  message?: string;
}

// ============================================================================
// Authentication Types
// ============================================================================

/**
 * Login request
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Login response with JWT tokens
 */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string; // ISO date string
  user: User;
}

/**
 * Registration request (for Mechanic and Intern roles only)
 */
export interface RegisterRequest {
  fullName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  role: UserRole.Mechanic | UserRole.Intern;
  birthDate?: string;
  ptoId: string;
  mentorId?: string;
  isMentor?: boolean;
  isRulesAccepted: boolean;
}

/**
 * Registration response with JWT tokens (enables auto-login after registration)
 * Returns the same structure as LoginResponse for seamless authentication
 */
export type RegisterResponse = LoginResponse;

/**
 * Refresh token request
 */
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Refresh token response
 */
export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

/**
 * Forgot password request
 */
export interface ForgotPasswordRequest {
  email: string;
}

/**
 * Forgot password response
 */
export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

/**
 * Verify OTP request
 */
export interface VerifyOtpRequest {
  email: string;
  otpCode: string;
}

/**
 * Verify OTP response
 */
export interface VerifyOtpResponse {
  success: boolean;
  resetToken?: string;
  message?: string;
  remainingAttempts?: number;
}

/**
 * Reset password request
 */
export interface ResetPasswordRequest {
  email: string;
  resetToken: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Verify mentor request
 */
export interface VerifyMentorRequest {
  mentorId?: string;
  fullName?: string;
}

/**
 * Verify mentor response
 */
export interface VerifyMentorResponse {
  found: boolean;
  mentor?: UserShort;
}

/**
 * Current authenticated user info
 */
export interface CurrentUser extends User {
  permissions: string[];
}

// ============================================================================
// Pagination and Sorting Types
// ============================================================================

/**
 * Pagination parameters for list requests
 */
export interface PaginationParams {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Sorting parameters for list requests
 */
export interface SortingParams {
  sortBy?: string;
  sortDescending?: boolean;
}

/**
 * Combined query parameters for list requests
 * Index signature allows assignment to Record<string, unknown>
 */
export interface ListQueryParams extends PaginationParams, SortingParams {
  search?: string;
  [key: string]: unknown;
}

// ============================================================================
// File Upload Types
// ============================================================================

/**
 * Server-side video compression state (video release). `None` for anything that is not
 * `video/*`, or when the pipeline is disabled. A `video/*` upload starts `Pending`; poll
 * `POST /api/lk/files/meta` or listen for the `FileProcessed` SignalR event to learn when it
 * flips to `Ready`. Mirrors the backend `FileProcessingStatus` enum (string over the wire).
 */
export type FileProcessingStatus = 'None' | 'Pending' | 'Processing' | 'Ready' | 'Failed';

/**
 * File DTO from backend
 */
export interface FileDto {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
  url: string;
  uploadedAt: string;
  /** Video release — see {@link FileProcessingStatus}. Absent on responses predating the field. */
  processingStatus?: FileProcessingStatus;
}

/**
 * `FileMetaDto` — batch response of `POST /api/lk/files/meta` / `POST /api/mobile/files/meta`
 * (video release). One round trip for every tile's current processing state, poster and
 * duration instead of a request per id. `posterUrl` is signed and minted fresh on every call —
 * never cache it beyond the response. `width`/`height`/`durationSeconds` are `null` until the
 * video reaches `Ready`. See `vault/api/files.md`.
 */
export interface FileMeta {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
  processingStatus: FileProcessingStatus;
  posterUrl: string | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
}

/**
 * File upload response from backend
 */
export interface FileUploadResponse {
  success: boolean;
  file?: FileDto;
  message: string;
}

/**
 * Multiple files upload response
 */
export interface FilesUploadResponse {
  files: FileDto[];
}

/**
 * File metadata (alias for FileDto)
 */
export interface FileMetadata {
  id: string;
  url: string;
  name: string;
  size: number;
  mimeType: string;
  uploadedAt: string;
}

// ============================================================================
// Document Generation Types
// ============================================================================

/**
 * Document types that can be generated
 */
export type DocumentType = 'act' | 'payroll' | 'invoice' | 'report';

/**
 * Document generation request
 */
export interface GenerateDocumentRequest {
  documentType: DocumentType;
  applicationPtoId?: string;
  applicationId?: string;
}

/**
 * Document generation response
 */
export interface GenerateDocumentResponse {
  documentUrl: string;
  documentType: DocumentType;
  generatedAt: string;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * ID parameter for single entity requests
 */
export interface IdParams {
  id: string;
}

/**
 * Date range parameters
 */
export interface DateRangeParams {
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Select option for dropdowns
 */
export interface SelectOption<T = string> {
  value: T;
  label: string;
}

/**
 * Generic lookup item
 */
export interface LookupItem {
  id: string;
  name: string;
}

// ============================================================================
// API Endpoints Constants
// ============================================================================

export const API_ENDPOINTS = {
  // Auth — все веб-эндпоинты живут под /api/lk/. Mobile использует /api/mobile/.
  AUTH: {
    LOGIN: '/api/lk/auth/login',
    REGISTER: '/api/lk/auth/register',
    REFRESH: '/api/lk/auth/refresh',
    FORGOT_PASSWORD: '/api/lk/auth/forgot-password',
    VERIFY_OTP: '/api/lk/auth/verify-otp',
    RESET_PASSWORD: '/api/lk/auth/reset-password',
    VERIFY_MENTOR: '/api/lk/auth/verify-mentor',
    REGISTRATION_PTO: '/api/lk/auth/registration/pto',
    REGISTRATION_MENTORS: '/api/lk/auth/registration/mentors',
    CHANGE_PASSWORD: '/api/lk/auth/change-password',
    FCM_TOKEN: '/api/lk/auth/fcm-token',
    FCM_TOKEN_LOGOUT: '/api/lk/auth/fcm-token/logout',
    LOGOUT: '/api/lk/auth/logout',
  },

  // Users — self-profile only (ADR-024: admin CRUD moved to WORKERS)
  USERS: {
    // Self-profile: GET/PUT /api/lk/users/me
    ME: '/api/lk/users/me',
    ME_FULL: '/api/lk/users/me/full',
    ME_PASSPORT: '/api/lk/users/me/passport',
    ME_INTERNATIONAL_PASSPORT: '/api/lk/users/me/international-passport',
    ME_DRIVERS_LICENSE: '/api/lk/users/me/drivers-license',
    ME_EDUCATION_EXPERIENCE: '/api/lk/users/me/education-experience',
    ME_EDUCATION_EXPERIENCE_BY_ID: (recordId: string) =>
      `/api/lk/users/me/education-experience/${recordId}`,
    ME_AVATAR: '/api/lk/users/me/avatar',
    ME_ADDITIONAL_EDUCATION: '/api/lk/users/me/additional-education',
    ME_ADDITIONAL_EDUCATION_BY_ID: (educationId: string) =>
      `/api/lk/users/me/additional-education/${educationId}`,
    ME_WORKWEAR: '/api/lk/users/me/workwear',
    ME_WORKWEAR_BY_ID: (recordId: string) => `/api/lk/users/me/workwear/${recordId}`,
    ME_CARS: '/api/lk/users/me/cars',
    ME_CARS_BY_ID: (recordId: string) => `/api/lk/users/me/cars/${recordId}`,
    // Member picker — approved-users search (LkAccess only, no Workers_View). Used by chat creation / add-member / mentions.
    SEARCH: '/api/lk/users/search',
  },

  // Workers — admin CRUD (ADR-024: split from /api/lk/users)
  WORKERS: {
    BASE: '/api/lk/workers',
    /** Quick-create a freelance mechanic — POST only, no BY_ID counterpart. */
    FREELANCER: '/api/lk/workers/freelancer',
    BY_ID: (id: string) => `/api/lk/workers/${id}`,
    FULL: (id: string) => `/api/lk/workers/${id}/full`,
    STATUS: (id: string) => `/api/lk/workers/${id}/status`,
    PASSPORT: (id: string) => `/api/lk/workers/${id}/passport`,
    INTERNATIONAL_PASSPORT: (id: string) => `/api/lk/workers/${id}/international-passport`,
    DRIVERS_LICENSE: (id: string) => `/api/lk/workers/${id}/drivers-license`,
    EDUCATION_EXPERIENCE: (id: string) => `/api/lk/workers/${id}/education-experience`,
    WORKWEAR: (id: string) => `/api/lk/workers/${id}/workwear`,
    RESET_PASSWORD: (id: string) => `/api/lk/workers/${id}/reset-password`,
    ADDITIONAL_EDUCATION: (id: string) => `/api/lk/workers/${id}/additional-education`,
    ADDITIONAL_EDUCATION_BY_ID: (id: string, educationId: string) =>
      `/api/lk/workers/${id}/additional-education/${educationId}`,
  },

  // Applications
  APPLICATIONS: {
    BASE: '/api/lk/applications',
    BY_ID: (id: string) => `/api/lk/applications/${id}`,
    CANCEL: (id: string) => `/api/lk/applications/${id}/cancel`,
    /** Merge of one customer's applications (application-merge spec): the comparison, POST with the selection. */
    MERGE_PREVIEW: '/api/lk/applications/merge/preview',
    /** The merge itself — one locked transaction; answers the target's `ApplicationFullDto`. */
    MERGE: '/api/lk/applications/merge',
    /** Duplicate hint of the forms: a POST with the picked units, containers and ПЛМ. */
    DUPLICATE_CHECK: '/api/lk/applications/duplicate-check',
    /** Duplicate notice of the card: the open applications sharing this one's units. */
    DUPLICATES: (id: string) => `/api/lk/applications/${id}/duplicates`,
    /** Replace the global item set (containers + equipment) and sync to all PTOs. */
    ITEMS: (id: string) => `/api/lk/applications/${id}/items`,
    PTO: (id: string) => `/api/lk/applications/${id}/pto`,
    PTO_REORDER: (id: string) => `/api/lk/applications/${id}/pto/reorder`,
    PTO_BY_ID: (id: string, ptoId: string) => `/api/lk/applications/${id}/pto/${ptoId}`,
    PTO_MECHANIC: (id: string, ptoId: string) => `/api/lk/applications/${id}/pto/${ptoId}/mechanic`,
    PTO_CLAIM: (id: string, ptoId: string) => `/api/lk/applications/${id}/pto/${ptoId}/claim`,
    PTO_UNCLAIM: (id: string, ptoId: string) => `/api/lk/applications/${id}/pto/${ptoId}/unclaim`,
    PTO_ITEMS: (id: string, ptoId: string) => `/api/lk/applications/${id}/pto/${ptoId}/items`,
    PTO_WORKS: (id: string, ptoId: string) => `/api/lk/applications/${id}/pto/${ptoId}/works`,
    PTO_COMPLETED_WORKS: (id: string, ptoId: string) =>
      `/api/lk/applications/${id}/pto/${ptoId}/completed-works`,
    PTO_WORKS_CONTAINER: (id: string, ptoId: string) =>
      `/api/lk/applications/${id}/pto/${ptoId}/works/container`,
    PTO_WORKS_CONTAINER_DELETE: (id: string, ptoId: string, containerId: string, serviceId: string) =>
      `/api/lk/applications/${id}/pto/${ptoId}/works/container/${containerId}/${serviceId}`,
    PTO_WORKS_EQUIPMENT: (id: string, ptoId: string) =>
      `/api/lk/applications/${id}/pto/${ptoId}/works/equipment`,
    PTO_WORKS_EQUIPMENT_DELETE: (id: string, ptoId: string, equipmentId: string, serviceId: string) =>
      `/api/lk/applications/${id}/pto/${ptoId}/works/equipment/${equipmentId}/${serviceId}`,
    // Container positions are created only through the inspection flow's
    // `containers/repaired` / `containers/needs-repair` routes (inspection redesign, Phase 4) —
    // there is no more quick-add/quick-create for containers.
    PTO_ITEMS_QUICK_ADD_EQUIPMENT: (id: string, ptoId: string) =>
      `/api/lk/applications/${id}/pto/${ptoId}/items/quick-add-equipment`,
    PTO_ITEMS_QUICK_CREATE_EQUIPMENT: (id: string, ptoId: string) =>
      `/api/lk/applications/${id}/pto/${ptoId}/items/quick-create-equipment`,
  },

  // Tasks
  TASKS: {
    BASE: '/api/lk/tasks',
    BY_ID: (id: string) => `/api/lk/tasks/${id}`,
    REPORT: (id: string) => `/api/lk/tasks/${id}/report`,
    CANCEL: (id: string) => `/api/lk/tasks/${id}/cancel`,
  },

  // Expenses
  EXPENSES: {
    BASE: '/api/lk/expenses',
    BY_ID: (id: string) => `/api/lk/expenses/${id}`,
    APPROVE: (id: string) => `/api/lk/expenses/${id}/approve`,
    REJECT: (id: string) => `/api/lk/expenses/${id}/reject`,
  },

  // FMO
  FMO: {
    PTO: '/api/lk/fmo/pto',
    PTO_BY_ID: (id: string) => `/api/lk/fmo/pto/${id}`,
    VERIFY: (id: string) => `/api/lk/fmo/pto/${id}/verify`,
    INVOICE: (id: string) => `/api/lk/fmo/pto/${id}/invoice`,
    PAYMENT_STATUS: (id: string) => `/api/lk/fmo/pto/${id}/payment-status`,
    INSPECTION: (id: string) => `/api/lk/fmo/pto/${id}/inspection`,
    DOCUMENTS: (id: string, type: DocumentType) => `/api/lk/fmo/pto/${id}/documents/${type}`,
    // Vision v4 Phase 3
    MILEAGE: (id: string) => `/api/lk/fmo/pto/${id}/mileage`,
    PAYMENT_ADJUSTMENTS: '/api/lk/fmo/payment-adjustments',
    // Vision v4 Phase 4
    UNLOCK: (id: string) => `/api/lk/fmo/pto/${id}/unlock-for-edit`,
  },

  // Directory - Customers
  CUSTOMERS: {
    BASE: '/api/lk/customers',
    BY_ID: (id: string) => `/api/lk/customers/${id}`,
  },

  // Directory - Containers
  CONTAINERS: {
    BASE: '/api/lk/containers',
    BY_ID: (id: string) => `/api/lk/containers/${id}`,
    INSPECTIONS: (id: string) => `/api/lk/containers/${id}/inspections`,
  },

  // Directory - Equipment
  EQUIPMENT: {
    BASE: '/api/lk/equipment',
    BY_ID: (id: string) => `/api/lk/equipment/${id}`,
    INSPECTIONS: (id: string) => `/api/lk/equipment/${id}/inspections`,
  },

  // Directory - PTO
  PTO: {
    BASE: '/api/lk/pto',
    BY_ID: (id: string) => `/api/lk/pto/${id}`,
  },

  // Directory - Services
  SERVICES: {
    BASE: '/api/lk/services',
    BY_ID: (id: string) => `/api/lk/services/${id}`,
  },

  // Directory - Directions
  DIRECTIONS: {
    BASE: '/api/lk/directions',
    BY_ID: (id: string) => `/api/lk/directions/${id}`,
  },

  // Directory - Materials
  MATERIALS: {
    BASE: '/api/lk/materials',
    BY_ID: (id: string) => `/api/lk/materials/${id}`,
  },

  // Directory - Instructions
  INSTRUCTIONS: {
    BASE: '/api/lk/instructions',
    BY_ID: (id: string) => `/api/lk/instructions/${id}`,
  },

  // Directory - Stations (Vision v4 Phase 1)
  STATIONS: {
    BASE: '/api/lk/directory/stations',
    BY_ID: (id: number) => `/api/lk/directory/stations/${id}`,
    BY_PTO: (ptoId: string) => `/api/lk/directory/stations/by-pto/${ptoId}`,
    MOBILE_BASE: '/api/mobile/stations',
  },

  // Directory - Terminals (Vision v4 Phase 1)
  TERMINALS: {
    BASE: '/api/lk/directory/terminals',
    BY_ID: (id: number) => `/api/lk/directory/terminals/${id}`,
    BY_PTO: (ptoId: string) => `/api/lk/directory/terminals/by-pto/${ptoId}`,
    MOBILE_BASE: '/api/mobile/terminals',
  },

  // Notifications
  NOTIFICATIONS: {
    BASE: '/api/lk/notifications',
    UNREAD_COUNT: '/api/lk/notifications/unread-count',
    READ: (id: string) => `/api/lk/notifications/${id}/read`,
    READ_ALL: '/api/lk/notifications/read-all',
  },

  // Files — все файловые операции под /api/lk/files
  FILES: {
    UPLOAD: '/api/lk/files/upload',
    BY_ID: (id: string) => `/api/lk/files/${id}`,
    METADATA: (id: string) => `/api/lk/files/${id}/metadata`,
    /** Batch lookup, video release — body `{ ids: string[] }` (1-200) -> `FileMeta[]`. */
    META: '/api/lk/files/meta',
  },

  // Audit Logs
  AUDIT_LOGS: {
    BASE: '/api/lk/audit-logs',
    BY_ID: (id: string) => `/api/lk/audit-logs/${id}`,
  },
} as const;

// ============================================================================
// SignalR Hub Endpoints
// ============================================================================

export const SIGNALR_HUBS = {
  NOTIFICATIONS: '/hubs/notifications',
  APPLICATIONS: '/hubs/applications',
  MESSENGER: '/hubs/messenger',
} as const;
